/**
 * Shared table and button styles for marketing pages.
 * Uses theme CSS variables for dark theme consistency.
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';

/** Shared content width for marketing tabs — keeps header + sections aligned and centered in Layout. */
export const MARKETING_PAGE_MAX_WIDTH = '1200px';

export const MarketingPageShell = styled.div`
  width: min(100%, ${MARKETING_PAGE_MAX_WIDTH});
  margin-left: auto;
  margin-right: auto;
  min-width: 0;
  box-sizing: border-box;
`;

/* ---- Table (admin/users style) ---- */
export const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`;

const TABLE_MOBILE_BREAKPOINT = '768px';
const TABLE_CARD_BREAKPOINT = '900px';

export const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: ${TABLE_MOBILE_BREAKPOINT}) {
    min-height: 0;
  }
`;

export const Table = styled.table<{ $mobileCards?: boolean }>`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
    vertical-align: middle;
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--muted);
    background: var(--surfaceVariant);
  }

  tbody tr:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }

  @media (max-width: ${TABLE_MOBILE_BREAKPOINT}) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }

  ${({ $mobileCards }) =>
    $mobileCards &&
    `
  @media (max-width: ${TABLE_CARD_BREAKPOINT}) {
    thead {
      display: none;
    }
    tbody tr {
      display: block;
      border: 1px solid var(--outline);
      border-radius: 8px;
      margin-bottom: 0.5rem;
      padding: 0.75rem;
      background: var(--surface);
    }
    tbody tr:hover {
      background-color: rgba(255, 255, 255, 0.04);
    }
    td {
      display: block;
      padding: 0.25rem 0;
      border: none;
    }
    td::before {
      content: attr(data-label);
      font-weight: 600;
      display: inline-block;
      margin-right: 0.5rem;
      min-width: 5em;
      color: var(--muted);
    }
  }
  `}
`;

export const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  color: var(--muted);
  font-size: 0.8rem;
  border-top: 1px solid var(--outline);
  background: var(--surface);
  @media (max-width: ${TABLE_MOBILE_BREAKPOINT}) {
    flex-wrap: wrap;
  }
`;

/* ---- Buttons (uniform padding: 14px 24px) ---- */
export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 14px 24px;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease;

  &:hover {
    background: var(--primaryDark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const PrimaryButtonLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 14px 24px;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  font-family: inherit;
  transition: background 0.2s ease;

  &:hover {
    background: var(--primaryDark);
    color: var(--onPrimary);
  }
`;

export const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 10px 18px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: var(--surfaceVariant);
    border-color: var(--outlineVariant);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/** Outline primary link (e.g. "Design SMS Funnel") */
export const SecondaryButtonLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 14px 24px;
  border: 1px solid var(--primary);
  background: var(--surfaceVariant);
  color: var(--primary);
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 8px;
  text-decoration: none;
  font-family: inherit;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: var(--surface);
    color: var(--primary);
  }
`;

export const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 10px 18px;
  border: 1px solid var(--error);
  background: var(--surface);
  color: var(--error);
  font-weight: 500;
  font-size: 0.875rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--errorContainer);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const PaginationButton = styled.button<{ $active?: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 0.55rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  font-size: 0.85rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? 'var(--primaryDark)' : 'var(--surfaceVariant)')};
    color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--primary)')};
    border-color: var(--outlineVariant);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
